import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';

export const WordsmithWordInput: React.FC = () => {
  const { socket, gameState, currentRoom } = useSocket();
  const t = useTranslations();
  const [word, setWord] = React.useState('');
  const [definition, setDefinition] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const isSubmitted = gameState?.wordsmithSubmitted || false;

  // Reset isSubmitting when submission is confirmed, phase changes, or word is set
  React.useEffect(() => {
    if (isSubmitted || currentRoom?.phase !== 'wordsmith_input' || gameState?.wordsmithWord) {
      setIsSubmitting(false);
    }
  }, [isSubmitted, currentRoom?.phase, gameState?.wordsmithWord]);

  const handleSubmit = () => {
    if (!socket || !word.trim() || !definition.trim() || isSubmitting || isSubmitted) return;
    
    setIsSubmitting(true);
    socket.emit('client:submit-word', word.trim(), definition.trim());
  };

  if (isSubmitted) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3 sm:mb-4 px-2">
            {t.games.deadpan.wordsmithInput}
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-2 px-2">
            {t.games.deadpan.wordsmithSubmitted}
          </p>
          {gameState?.wordsmithWord && (
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">{t.games.deadpan.theWord}:</p>
              <div className="w-full overflow-x-auto">
                <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 whitespace-nowrap">{gameState.wordsmithWord}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      <div className="text-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 px-2">
          {t.games.deadpan.wordsmithInput}
        </h2>
        <p className="text-base sm:text-lg text-gray-700 mb-3 sm:mb-4 px-2">{t.games.deadpan.wordsmithInstructions}</p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            {t.games.deadpan.theWord}
          </label>
          <input
            type="text"
            value={word}
            onChange={(e) => setWord(e.target.value)}
            placeholder={t.games.deadpan.wordsmithWordPlaceholder}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none text-base sm:text-lg"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            {t.games.deadpan.realDefinition}
          </label>
          <textarea
            value={definition}
            onChange={(e) => setDefinition(e.target.value)}
            placeholder={t.games.deadpan.wordsmithDefinitionPlaceholder}
            rows={4}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none text-base sm:text-lg resize-none"
            disabled={isSubmitting}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={!word.trim() || !definition.trim() || isSubmitting}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {isSubmitting ? t.ui.creating : t.games.deadpan.wordsmithSubmit}
        </button>
      </div>
    </div>
  );
};

